import React, { useState, useEffect } from 'react';
import { Settings as SettingsIcon, Save, Play, StopCircle, Loader, Mail, Database } from 'lucide-react';
import { dashboardApi } from '../services/api';

const Settings = () => {
  const [settings, setSettings] = useState({
    min_followers: '',
    max_followers: '',
    min_engagement_rate: '',
    target_countries: '',
    daily_email_limit: '',
    run_interval_hours: '',
    fresh_scraping_interval: '',
    scraper_run_counter: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scraperStatus, setScraperStatus] = useState({ running: false, mode: 'scraper-only' });
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [selectedFlow, setSelectedFlow] = useState('scraper-only');

  useEffect(() => {
    loadSettings();
    loadScraperStatus();

    // Check scraper status every 5 seconds
    const interval = setInterval(loadScraperStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadSettings = async () => {
    try {
      const response = await dashboardApi.getSettings();
      const settingsObj = {};
      Object.keys(response.data).forEach(key => {
        settingsObj[key] = response.data[key].value;
      });
      setSettings(settingsObj);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load settings:', error);
      setLoading(false);
    }
  };

  const loadScraperStatus = async () => {
    try {
      setCheckingStatus(true);
      const response = await dashboardApi.getScraperStatus();
      setScraperStatus(response.data);
      setCheckingStatus(false);
    } catch (error) {
      console.error('Failed to load scraper status:', error);
      setCheckingStatus(false);
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await dashboardApi.updateSettings(settings);
      alert('Settings saved successfully!');
      setSaving(false);
    } catch (error) {
      console.error('Failed to save settings:', error);
      alert('Failed to save settings');
      setSaving(false);
    }
  };

  const handleStartScraper = async () => {
    try {
      const response = await dashboardApi.startScraper(selectedFlow);
      if (response.data.success) {
        const flowName = selectedFlow === 'complete' ? 'Complete Flow' : 'Dataset Scraping';
        alert(`${flowName} started! Check the Dashboard for updates.`);
        loadScraperStatus();
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      console.error('Failed to start scraper:', error);
      alert('Failed to start scraper');
    }
  };

  const handleInputChange = (key, value) => {
    setSettings({ ...settings, [key]: value });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Settings</h2>
          <p className="text-gray-600 mt-2">Configure discovery criteria and scraping settings</p>
        </div>
      </div>

      {/* Scraper Control Panel */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold">Discovery Pipeline Control</h3>
            <p className="text-sm text-gray-600 mt-1">
              Choose your workflow and start the discovery process
            </p>
          </div>
          <div className="flex items-center space-x-3">
            {checkingStatus && <Loader className="w-5 h-5 text-gray-400 animate-spin" />}
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${
              scraperStatus.running
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-800'
            }`}>
              {scraperStatus.running ? 'Running' : 'Idle'}
            </div>
          </div>
        </div>

        {/* Flow Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Select Workflow
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Dataset Scrape Only */}
            <div
              onClick={() => !scraperStatus.running && setSelectedFlow('scraper-only')}
              className={`relative border-2 rounded-lg p-4 cursor-pointer transition-all ${
                selectedFlow === 'scraper-only'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-300'
              } ${scraperStatus.running ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className="flex items-start">
                <div className={`p-2 rounded-lg ${
                  selectedFlow === 'scraper-only' ? 'bg-blue-100' : 'bg-gray-100'
                }`}>
                  <Database className={`w-5 h-5 ${
                    selectedFlow === 'scraper-only' ? 'text-blue-600' : 'text-gray-600'
                  }`} />
                </div>
                <div className="ml-3 flex-1">
                  <h4 className="font-semibold text-gray-900">Dataset Scrape Only</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Collect and filter influencer data without contact
                  </p>
                  <ul className="text-xs text-gray-500 mt-2 space-y-1">
                    <li>✓ Fetch profiles from Apify</li>
                    <li>✓ Apply qualification filters</li>
                    <li>✓ Save to database</li>
                    <li>✗ No email finding</li>
                    <li>✗ No outreach</li>
                  </ul>
                </div>
              </div>
              {selectedFlow === 'scraper-only' && (
                <div className="absolute top-3 right-3">
                  <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              )}
            </div>

            {/* Complete Flow */}
            <div
              onClick={() => !scraperStatus.running && setSelectedFlow('complete')}
              className={`relative border-2 rounded-lg p-4 cursor-pointer transition-all ${
                selectedFlow === 'complete'
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-green-300'
              } ${scraperStatus.running ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className="flex items-start">
                <div className={`p-2 rounded-lg ${
                  selectedFlow === 'complete' ? 'bg-green-100' : 'bg-gray-100'
                }`}>
                  <Mail className={`w-5 h-5 ${
                    selectedFlow === 'complete' ? 'text-green-600' : 'text-gray-600'
                  }`} />
                </div>
                <div className="ml-3 flex-1">
                  <h4 className="font-semibold text-gray-900">Complete Flow</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Full automation: scrape, find emails, and reach out
                  </p>
                  <ul className="text-xs text-gray-500 mt-2 space-y-1">
                    <li>✓ Fetch profiles from Apify</li>
                    <li>✓ Apply qualification filters</li>
                    <li>✓ Find email addresses</li>
                    <li>✓ Validate emails</li>
                    <li>✓ Send outreach messages</li>
                  </ul>
                </div>
              </div>
              {selectedFlow === 'complete' && (
                <div className="absolute top-3 right-3">
                  <div className="w-5 h-5 bg-green-600 rounded-full flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Start Button */}
        <div className="border-t border-gray-200 pt-4">
          <button
            onClick={handleStartScraper}
            disabled={scraperStatus.running}
            className={`flex items-center px-6 py-3 rounded-lg font-medium transition-colors ${
              scraperStatus.running
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : selectedFlow === 'complete'
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {scraperStatus.running ? (
              <>
                <Loader className="w-5 h-5 mr-2 animate-spin" />
                {scraperStatus.mode === 'complete' ? 'Complete Flow Running...' : 'Dataset Scraping...'}
              </>
            ) : (
              <>
                <Play className="w-5 h-5 mr-2" />
                {selectedFlow === 'complete' ? 'Start Complete Flow' : 'Start Dataset Scraping'}
              </>
            )}
          </button>

          {scraperStatus.running && (
            <div className={`mt-3 p-3 rounded-lg ${
              scraperStatus.mode === 'complete' ? 'bg-green-50' : 'bg-blue-50'
            }`}>
              <p className="text-sm text-gray-700">
                <strong>{scraperStatus.mode === 'complete' ? 'Complete Flow' : 'Dataset Scraping'}</strong> is currently running.
                Check the Dashboard for real-time updates.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Settings Form */}
      <form onSubmit={handleSaveSettings}>
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold flex items-center">
              <SettingsIcon className="w-5 h-5 mr-2" />
              Discovery Criteria
            </h3>
          </div>

          <div className="p-6 space-y-6">
            {/* Follower Range */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Minimum Followers
                </label>
                <input
                  type="number"
                  value={settings.min_followers}
                  onChange={(e) => handleInputChange('min_followers', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="100000"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Minimum follower count (e.g., 100000)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Maximum Followers
                </label>
                <input
                  type="number"
                  value={settings.max_followers}
                  onChange={(e) => handleInputChange('max_followers', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="5000000"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Maximum follower count (e.g., 5000000)
                </p>
              </div>
            </div>

            {/* Engagement Rate */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minimum Engagement Rate (%)
              </label>
              <input
                type="number"
                step="0.1"
                value={settings.min_engagement_rate}
                onChange={(e) => handleInputChange('min_engagement_rate', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="2.0"
              />
              <p className="text-xs text-gray-500 mt-1">
                Minimum engagement rate percentage (e.g., 2.0)
              </p>
            </div>

            {/* Target Countries */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target Countries
              </label>
              <input
                type="text"
                value={settings.target_countries}
                onChange={(e) => handleInputChange('target_countries', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="CA,AU"
              />
              <p className="text-xs text-gray-500 mt-1">
                Comma-separated country codes (e.g., CA,AU for Canada and Australia)
              </p>
            </div>

            {/* Daily Email Limit */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Daily Email Limit
              </label>
              <input
                type="number"
                value={settings.daily_email_limit}
                onChange={(e) => handleInputChange('daily_email_limit', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="50"
              />
              <p className="text-xs text-gray-500 mt-1">
                Maximum emails to send per day (not used in scraper-only mode)
              </p>
            </div>

            {/* Run Interval */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Run Interval (Hours)
              </label>
              <input
                type="number"
                value={settings.run_interval_hours}
                onChange={(e) => handleInputChange('run_interval_hours', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="5"
              />
              <p className="text-xs text-gray-500 mt-1">
                Hours between automated discovery runs (when scheduler is enabled)
              </p>
            </div>

            {/* Fresh Scraping Interval */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fresh Scraping Interval
              </label>
              <div className="flex items-center space-x-3">
                <input
                  type="number"
                  min="1"
                  value={settings.fresh_scraping_interval}
                  onChange={(e) => handleInputChange('fresh_scraping_interval', e.target.value)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="5"
                />
                <span className="text-sm text-gray-600">runs</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Trigger fresh Apify scraping every N runs (default: 5). Other runs use historical data to save costs.
              </p>
              {settings.scraper_run_counter && (
                <p className="text-xs text-blue-600 mt-2">
                  Current: Run {settings.scraper_run_counter}/{settings.fresh_scraping_interval}
                  {settings.scraper_run_counter >= (settings.fresh_scraping_interval - 1) ?
                    ' (Next run will trigger fresh scraping)' :
                    ` (Fresh scraping in ${settings.fresh_scraping_interval - settings.scraper_run_counter} runs)`
                  }
                </p>
              )}
            </div>
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className={`flex items-center px-6 py-2 rounded-lg font-medium transition-colors ${
                saving
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              } text-white`}
            >
              {saving ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Settings
                </>
              )}
            </button>
          </div>
        </div>
      </form>

      {/* Info Panels */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h4 className="font-semibold text-blue-900 mb-2">📊 Dataset Scrape Only (Smart Mode)</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• <strong>First run:</strong> Fetches historical data if database is empty</li>
            <li>• <strong>Runs 1-4:</strong> Uses historical data (fast, no Apify costs)</li>
            <li>• <strong>Run 5:</strong> Triggers fresh Apify scraping (gets new data)</li>
            <li>• <strong>Cycle repeats:</strong> Saves costs while staying up-to-date</li>
            <li>• Automatically filters and qualifies profiles</li>
            <li>• No email finding or outreach</li>
          </ul>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <h4 className="font-semibold text-green-900 mb-2">🚀 Complete Flow</h4>
          <ul className="text-sm text-green-800 space-y-1">
            <li>• Runs the full discovery pipeline end-to-end</li>
            <li>• Finds and validates email addresses</li>
            <li>• Sends personalized outreach messages</li>
            <li>• Respects daily email limits from settings</li>
            <li>• Logs all activities and responses</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Settings;
